// ============================================================
// WorkspaceShell — the workspace tier of the auth → tenant →
// workspace flow. Rendered for /w/:tenantId: resolves the active
// workspace from memberships + relationships, writes X-Tenant-Id,
// and hosts the sidebar + content area. Switching tenants through the account menu
// follows the destination URL and refreshes the tenant context.
// ============================================================

import { useLayoutEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Navigate,
  Outlet,
  useLocation,
  useParams,
} from '@tanstack/react-router';
import { Building2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Separator } from '@/components/ui/separator';
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from '@/components/ui/sidebar';

import { useIdentity, useRelationships } from '@/features/auth/hooks/useAuth';
import { WorkspaceResponsibilityGate } from '@/features/onboarding/components/WorkspaceResponsibilityGate';
import { StudentProfileOnboardingGate } from '@/features/student-profile/components/StudentProfileOnboardingGate';
import { groupWorkspaces, roleLabel } from '@/features/auth/model/memberships';
import {
  readTenantId,
  writeTenantId,
} from '@/features/auth/model/tenant.store';

import { ThemeToggle } from '@/components/theme/theme-toggle';
import { LanguageSwitcher } from '@/components/i18n/LanguageSwitcher';
import { roleMessageKey } from '@/i18n/role';
import { useAppConfig } from '@/app-config/runtime/use-app-config';
import { resolveWorkspaceNavigationPlacement } from '@/app-config/routing/workspace-navigation-layout';
import { cn } from '@/lib/utils';

import { AppSidebar } from './AppSidebar';
import { NotificationBell } from './NotificationBell';
import { isTenantSensitiveQueryKey } from './workspace-cache';

import { AppHeaderNavigation } from './AppHeaderNavigation';
import { WorkspaceAccountMenu } from './WorkspaceAccountMenu';

export function WorkspaceShell() {
  const { tenantId = '' } = useParams({ strict: false });
  const { pathname } = useLocation();
  const { t } = useTranslation('shell');
  const appConfig = useAppConfig();

  const queryClient = useQueryClient();
  const identityQuery = useIdentity();
  const relationshipsQuery = useRelationships();

  const workspaces = useMemo(
    () =>
      groupWorkspaces(
        identityQuery.data?.memberships ?? [],
        relationshipsQuery.data ?? [],
      ),
    [identityQuery.data, relationshipsQuery.data],
  );
  const active = workspaces.find(
    (workspace) => workspace.tenantId === tenantId,
  );

  // The tenant context follows the URL: every workspace entry writes
  // X-Tenant-Id (all tenant-scoped clients read this store).
  useLayoutEffect(() => {
    if (!tenantId) return;
    if (readTenantId() !== tenantId) {
      queryClient.removeQueries({
        predicate: (query) => isTenantSensitiveQueryKey(query.queryKey),
      });
    }
    writeTenantId(tenantId);
  }, [queryClient, tenantId]);

  // 身份数据加载中：布局骨架照常渲染（侧栏/顶栏占位），内容区显示
  // 进入中状态——刷新时不再整页跳变为"正在进入工作区"的独立页面。
  const loading = identityQuery.isLoading || relationshipsQuery.isLoading;
  const navigationPlacement = resolveWorkspaceNavigationPlacement(
    appConfig,
    pathname,
    tenantId,
  );
  const headerNavigation = navigationPlacement === 'header';
  const [sidebarState, setSidebarState] = useState({
    headerNavigation,
    open: !headerNavigation,
  });
  const navigationChanged = sidebarState.headerNavigation !== headerNavigation;
  const sidebarOpen = navigationChanged ? !headerNavigation : sidebarState.open;

  // Reset the shell chrome when entering or leaving AI workspace while
  // retaining manual collapse state between renders on regular routes.
  if (navigationChanged) {
    setSidebarState({ headerNavigation, open: !headerNavigation });
  }

  if (!loading && !active) {
    return <Navigate to="/workspaces" replace />;
  }

  return (
    <SidebarProvider
      open={sidebarOpen}
      onOpenChange={(open) => setSidebarState({ headerNavigation, open })}
      className={cn(
        'h-svh overflow-hidden',
        headerNavigation ? 'bg-background' : 'bg-sidebar',
      )}
    >
      <WorkspaceResponsibilityGate
        key={`${identityQuery.data?.userId ?? ''}:${active?.tenantId ?? ''}`}
        accountId={identityQuery.data?.userId}
        tenantId={active?.tenantId}
        roles={active?.roles ?? []}
      />
      <StudentProfileOnboardingGate enabled={active?.roles.includes('student') ?? false} />
      <AppSidebar
        workspaceName={active?.name ?? t('loading')}
        collapsible={headerNavigation ? 'offcanvas' : 'icon'}
      />
      <SidebarInset
        className={cn(
          'min-h-0 min-w-0 overflow-hidden bg-(--page-surface-bg)',
          !headerNavigation &&
            'md:my-2 md:ml-2 md:mr-2 md:rounded-xl md:border md:shadow-sm',
        )}
      >
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <header className="flex h-(--page-header-height) shrink-0 items-center gap-2 border-b border-border bg-(--page-surface-bg) px-4">
            {headerNavigation ? (
              <div className="min-w-0 animate-in fade-in slide-in-from-left-2 duration-200">
                <AppHeaderNavigation />
              </div>
            ) : (
              <>
                <SidebarTrigger className="-ml-1" />
                <Separator
                  orientation="vertical"
                  className="mr-2 h-4 self-center"
                />
                <span className="flex min-w-0 items-center gap-1.5 text-sm font-medium">
                  <Building2 className="size-4 shrink-0 text-muted-foreground" />
                  <span className="truncate">
                    {active?.name ?? t('loading')}
                  </span>
                </span>
                <span className="hidden truncate border-l pl-3 text-xs text-muted-foreground sm:inline">
                  {active?.roles
                    .map((role) => {
                      const key = roleMessageKey(role);
                      return key ? t(key) : roleLabel(role);
                    })
                    .join(' · ') ?? ''}
                </span>
              </>
            )}
            <div className="ml-auto flex shrink-0 items-center gap-1">
              <NotificationBell />
              <ThemeToggle />
              <LanguageSwitcher />
              {headerNavigation ? (
                <WorkspaceAccountMenu presentation="header" />
              ) : null}
            </div>
          </header>
          <div className="workspace-scroll-container min-h-0 flex-1 overflow-y-auto">
            {loading ? (
              <div className="grid h-full place-items-center text-sm text-muted-foreground">
                {t('enteringWorkspace')}
              </div>
            ) : (
              <Outlet />
            )}
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
